import { useMemo, useState } from 'react'
import { FaDownload, FaFileAlt } from 'react-icons/fa'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../../../helpers/apiRequester'
import styles from './styles.module.scss'
import {
    DEFAULT_REPORT_COLUMNS,
    REPORT_COLUMN_GROUPS,
    REPORT_COLUMNS,
    type ReportColumnGroup,
} from './reportColumns'

interface FundingBatchLike {
    batch_id: string
    total_clients?: number
    created_at?: string
    allocation_date?: string
    config?: {
        nera_id?: string
    }
    [key: string]: unknown
}

type InclusionFilter = 'accepted' | 'removed' | 'all'
type RowLevel = 'clients' | 'claims'
type FileFormat = 'csv' | 'xlsx'

interface BatchReportBuilderProps {
    fundingBatches: FundingBatchLike[]
}

const formatBatchDate = (batch: FundingBatchLike): string => {
    const raw = batch.created_at || batch.allocation_date
    if (!raw) return ''
    const date = new Date(raw)
    if (Number.isNaN(date.getTime())) return ''
    return date.toLocaleDateString('en-GB')
}

export const BatchReportBuilder = ({ fundingBatches }: BatchReportBuilderProps) => {
    const [selectedBatchIds, setSelectedBatchIds] = useState<string[]>([])
    const [selectedColumns, setSelectedColumns] = useState<string[]>(DEFAULT_REPORT_COLUMNS)
    const [inclusion, setInclusion] = useState<InclusionFilter>('accepted')
    const [rowLevel, setRowLevel] = useState<RowLevel>('clients')
    const [fileFormat, setFileFormat] = useState<FileFormat>('csv')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [result, setResult] = useState<{ recordCount: number; batchCount: number } | null>(null)

    const orderedColumns = useMemo(
        () => REPORT_COLUMNS.filter((column) => selectedColumns.includes(column.key)).map((column) => column.key),
        [selectedColumns]
    )

    const toggleBatch = (batchId: string) => {
        setSelectedBatchIds((current) =>
            current.includes(batchId) ? current.filter((id) => id !== batchId) : [...current, batchId]
        )
        setResult(null)
    }

    const toggleColumn = (key: string) => {
        setSelectedColumns((current) =>
            current.includes(key) ? current.filter((id) => id !== key) : [...current, key]
        )
        setResult(null)
    }

    const selectAllBatches = () => {
        setSelectedBatchIds(fundingBatches.map((batch) => batch.batch_id))
        setResult(null)
    }

    const selectGroupColumns = (group: ReportColumnGroup) => {
        const groupKeys = REPORT_COLUMNS.filter((column) => column.group === group).map((column) => column.key)
        setSelectedColumns((current) => Array.from(new Set([...current, ...groupKeys])))
        setResult(null)
    }

    const handleDownload = async () => {
        if (selectedBatchIds.length === 0 || orderedColumns.length === 0) return

        try {
            setLoading(true)
            setError(null)
            const response = await api({
                url: '/funding-batch-custom-export',
                data: {
                    batchIds: selectedBatchIds,
                    columns: orderedColumns,
                    inclusion,
                    rowLevel,
                    asExcel: fileFormat === 'xlsx',
                },
            })

            if (response.data?.success && response.data.fileUrl) {
                window.open(response.data.fileUrl, '_blank')
                setResult({
                    recordCount: response.data.recordCount || 0,
                    batchCount: response.data.batchCount || selectedBatchIds.length,
                })
            } else {
                setError(response.data?.message || 'Failed to generate report')
            }
        } catch (err: any) {
            const message = err?.response?.data?.message || err?.message || 'Failed to generate report'
            setError(message)
            Report.failure('Report failed', message, 'OK')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className={`${styles.batchControlCard} ${styles.batchControlCardWide}`}>
            <div className={styles.batchControlCardHeader}>
                <span className={`${styles.batchControlBadge} ${styles.badgeReport}`}>
                    <FaFileAlt />
                </span>
                <div>
                    <h4>Build and download a report</h4>
                    <p>
                        Choose funding batches and columns, then download a CSV or Excel file. Accepted rows are claims
                        that have not been audit-removed.
                    </p>
                </div>
            </div>

            <div className={styles.reportBuilderStack}>
                <div className={styles.reportBuilderPanel}>
                    <div className={styles.reportPanelHeader}>
                        <label>
                            Funding batches ({selectedBatchIds.length} of {fundingBatches.length} selected)
                        </label>
                        <div className={styles.reportPanelActions}>
                            <button type="button" onClick={selectAllBatches}>
                                Select all
                            </button>
                            <button type="button" onClick={() => setSelectedBatchIds([])}>
                                Clear
                            </button>
                        </div>
                    </div>
                    {fundingBatches.length === 0 ? (
                        <p className={styles.reportEmptyText}>No funding batches available.</p>
                    ) : (
                        <div className={styles.reportBatchGrid}>
                            {fundingBatches.map((batch) => (
                                <label key={batch.batch_id} className={styles.reportCheckRow}>
                                    <input
                                        type="checkbox"
                                        checked={selectedBatchIds.includes(batch.batch_id)}
                                        onChange={() => toggleBatch(batch.batch_id)}
                                    />
                                    <span>
                                        <strong>{batch.batch_id}</strong>
                                        <small>
                                            {batch.config?.nera_id ? `Nera ${batch.config.nera_id} · ` : ''}
                                            {(batch.total_clients || 0).toLocaleString()} clients
                                            {formatBatchDate(batch) ? ` · ${formatBatchDate(batch)}` : ''}
                                        </small>
                                    </span>
                                </label>
                            ))}
                        </div>
                    )}
                </div>

                <div className={styles.reportBuilderPanel}>
                    <div className={styles.reportPanelHeader}>
                        <label>Columns ({selectedColumns.length} selected)</label>
                        <div className={styles.reportPanelActions}>
                            <button type="button" onClick={() => setSelectedColumns(REPORT_COLUMNS.map((c) => c.key))}>
                                Select all
                            </button>
                            <button type="button" onClick={() => setSelectedColumns([])}>
                                Clear
                            </button>
                        </div>
                    </div>
                    {REPORT_COLUMN_GROUPS.map((group) => (
                        <div key={group.id} className={styles.reportColumnGroup}>
                            <div className={styles.reportColumnGroupHeader}>
                                <span>{group.label}</span>
                                <button type="button" onClick={() => selectGroupColumns(group.id)}>
                                    Add group
                                </button>
                            </div>
                            <div className={styles.reportColumnsGrid}>
                                {REPORT_COLUMNS.filter((column) => column.group === group.id).map((column) => (
                                    <label key={column.key} className={styles.reportCheckRow}>
                                        <input
                                            type="checkbox"
                                            checked={selectedColumns.includes(column.key)}
                                            onChange={() => toggleColumn(column.key)}
                                        />
                                        <span>{column.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className={styles.reportOptionsRow}>
                <div className={styles.reportOptionGroup}>
                    <span>Include</span>
                    <div className={styles.reportSegmented}>
                        {(
                            [
                                { id: 'accepted', label: 'Accepted only' },
                                { id: 'removed', label: 'Audit removed' },
                                { id: 'all', label: 'All rows' },
                            ] as { id: InclusionFilter; label: string }[]
                        ).map((option) => (
                            <button
                                key={option.id}
                                type="button"
                                className={inclusion === option.id ? styles.active : ''}
                                onClick={() => {
                                    setInclusion(option.id)
                                    setResult(null)
                                }}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className={styles.reportOptionGroup}>
                    <span>Rows</span>
                    <div className={styles.reportSegmented}>
                        {(
                            [
                                { id: 'clients', label: 'One per client' },
                                { id: 'claims', label: 'One per claim' },
                            ] as { id: RowLevel; label: string }[]
                        ).map((option) => (
                            <button
                                key={option.id}
                                type="button"
                                className={rowLevel === option.id ? styles.active : ''}
                                onClick={() => {
                                    setRowLevel(option.id)
                                    setResult(null)
                                }}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className={styles.reportOptionGroup}>
                    <span>Format</span>
                    <div className={styles.reportSegmented}>
                        {(
                            [
                                { id: 'csv', label: 'CSV' },
                                { id: 'xlsx', label: 'Excel' },
                            ] as { id: FileFormat; label: string }[]
                        ).map((option) => (
                            <button
                                key={option.id}
                                type="button"
                                className={fileFormat === option.id ? styles.active : ''}
                                onClick={() => setFileFormat(option.id)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {error && <p className={styles.importErrorText}>{error}</p>}

            {result && (
                <p className={styles.importStatusText}>
                    Downloaded {result.recordCount.toLocaleString()} row
                    {result.recordCount === 1 ? '' : 's'} from {result.batchCount} batch
                    {result.batchCount === 1 ? '' : 'es'}.
                </p>
            )}

            <div className={styles.importActions}>
                <button
                    type="button"
                    className={styles.importButton}
                    onClick={handleDownload}
                    disabled={loading || selectedBatchIds.length === 0 || selectedColumns.length === 0}
                >
                    <FaDownload style={{ marginRight: 8 }} />
                    {loading ? 'Building report…' : 'Download report'}
                </button>
            </div>
        </div>
    )
}
